import { SUMMARISING_OUTPUT_TYPE } from "../types/constants";
import { summaryColumnTypes } from "../utils/filterKindUtils";
import { useCallback, useRef, useState } from "react";
import * as reportsV3Service from "services/reportsV3Service";

// Lazily runs a SUMMARISING report over the pre-aggregation data, refetching
// only when the summary report or asAt changes.
export const useInputFilterSummary = ({ buildSummaryReport, asAt }) => {
  const [runImmediateReport] = reportsV3Service.useRunImmediateReportMutation();

  const [summary, setSummary] = useState(null);
  const [columnTypes, setColumnTypes] = useState({});
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [settled, setSettled] = useState(false);

  const requestIdRef = useRef(0);
  const fingerprintRef = useRef(null);
  const latestRef = useRef({});
  latestRef.current = { buildSummaryReport, asAt, runImmediateReport };

  const ensureLoaded = useCallback(async () => {
    const {
      buildSummaryReport: build,
      asAt: at,
      runImmediateReport: run,
    } = latestRef.current;
    const report = build();
    if (!report) return;
    const fingerprint = JSON.stringify({ report, asAt: at });
    if (fingerprint === fingerprintRef.current) return;
    fingerprintRef.current = fingerprint;

    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const data = await run({
        report,
        outputType: SUMMARISING_OUTPUT_TYPE,
        ...(at ? { asAt: at } : {}),
      }).unwrap();
      if (requestId !== requestIdRef.current) return;
      setSummary(data[0]?.tableSummary ?? null);
      setColumnTypes(summaryColumnTypes(data[0]?.columns));
      setFailed(false);
      setSettled(true);
      setLoading(false);
    } catch {
      if (requestId !== requestIdRef.current) return;
      fingerprintRef.current = null;
      setSummary(null);
      setColumnTypes({});
      setFailed(true);
      setSettled(true);
      setLoading(false);
    }
  }, []);

  return { summary, columnTypes, loading, failed, settled, ensureLoaded };
};
